/**
 * QR menu (view only) — the SYSTEM tag catalogue, mirrored from
 * pos-api/src/qr-view/tags.ts. Pinned by pos-api/src/qr-view/__tests__/resolve.test.ts:
 * change both together.
 */

export type TagKind = 'badge' | 'dietary' | 'allergen';

export interface SystemTag {
  code: string;
  kind: TagKind;
  en: string;
  ar: string;
  /** Tabler icon name (the icon font the menus already load). */
  icon: string;
}

export const SYSTEM_TAGS: readonly SystemTag[] = [
  { code: 'new', kind: 'badge', en: 'New', ar: 'جديد', icon: 'sparkles' },
  { code: 'popular', kind: 'badge', en: 'Popular', ar: 'الأكثر طلباً', icon: 'flame' },
  { code: 'chef_pick', kind: 'badge', en: "Chef's pick", ar: 'اختيار الشيف', icon: 'chef-hat' },
  { code: 'signature', kind: 'badge', en: 'Signature', ar: 'طبق مميز', icon: 'award' },
  { code: 'limited', kind: 'badge', en: 'Limited time', ar: 'لفترة محدودة', icon: 'clock' },

  { code: 'vegetarian', kind: 'dietary', en: 'Vegetarian', ar: 'نباتي', icon: 'leaf' },
  { code: 'vegan', kind: 'dietary', en: 'Vegan', ar: 'نباتي صرف', icon: 'plant' },
  { code: 'gluten_free', kind: 'dietary', en: 'Gluten-free', ar: 'خالٍ من الجلوتين', icon: 'wheat-off' },
  { code: 'halal', kind: 'dietary', en: 'Halal', ar: 'حلال', icon: 'certificate' },
  { code: 'dairy_free', kind: 'dietary', en: 'Dairy-free', ar: 'خالٍ من الألبان', icon: 'milk-off' },
  { code: 'sugar_free', kind: 'dietary', en: 'Sugar-free', ar: 'بدون سكر', icon: 'cube-off' },

  { code: 'contains_nuts', kind: 'allergen', en: 'Contains nuts', ar: 'يحتوي على مكسرات', icon: 'nut' },
  { code: 'contains_peanuts', kind: 'allergen', en: 'Contains peanuts', ar: 'يحتوي على فول سوداني', icon: 'nut' },
  { code: 'contains_dairy', kind: 'allergen', en: 'Contains dairy', ar: 'يحتوي على ألبان', icon: 'milk' },
  { code: 'contains_eggs', kind: 'allergen', en: 'Contains eggs', ar: 'يحتوي على بيض', icon: 'egg' },
  { code: 'contains_gluten', kind: 'allergen', en: 'Contains gluten', ar: 'يحتوي على جلوتين', icon: 'wheat' },
  { code: 'contains_shellfish', kind: 'allergen', en: 'Contains shellfish', ar: 'يحتوي على قشريات', icon: 'shrimp' },
  { code: 'contains_fish', kind: 'allergen', en: 'Contains fish', ar: 'يحتوي على سمك', icon: 'fish' },
  { code: 'contains_soy', kind: 'allergen', en: 'Contains soy', ar: 'يحتوي على صويا', icon: 'seedling' },
  { code: 'contains_sesame', kind: 'allergen', en: 'Contains sesame', ar: 'يحتوي على سمسم', icon: 'grain' },
];

const BY_CODE = new Map(SYSTEM_TAGS.map((t) => [t.code, t]));

export function systemTag(code: string): SystemTag | undefined {
  return BY_CODE.get(code);
}

/** 'c12' → 12 for a custom tag code; null for a system code or junk. */
export function customTagId(code: string): number | null {
  const m = /^c(\d+)$/.exec(code);
  return m ? Number(m[1]) : null;
}

/** True when `code` is a system tag or a well-formed custom tag code. */
export function isValidTagCode(code: string): boolean {
  return BY_CODE.has(code) || customTagId(code) != null;
}

// ── Search normalisation (mirrors pos-api/src/qr-view/resolve.ts
//    normalizeForSearch + utils/arabic.ts normalizeAr; pinned by a test) ────

/** Arabic + Latin search normalisation: same rules as the server, so on-device search matches like the API would. */
export function normalizeForSearch(text: string | null | undefined): string {
  if (!text) return '';
  return text
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .replace(/[أإآٱ]/g, 'ا')
    .replace(/ى/g, 'ي')
    .replace(/ة/g, 'ه')
    .replace(/[ؤئ]/g, 'ء')
    .replace(/[ًٌٍَُِّْ]/g, '')
    .toLowerCase()
    .replace(/ـ/g, '')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

/** Every query word appears in the haystack, any order, partial words allowed. */
export function matchesSearch(haystack: string, query: string): boolean {
  const h = normalizeForSearch(haystack);
  const words = normalizeForSearch(query).split(' ').filter(Boolean);
  return words.length > 0 && words.every((w) => h.includes(w));
}

// ── The public menu (mirrors pos-api/src/qr-view/public.types.ts) ─────────

export interface QvBi { en: string | null; ar: string | null }
export interface QvImage { w320: string | null; w640: string | null; w1280: string | null; color: string | null }
export interface QvUnit { id: number; name: QvBi; price: number; was_price: number | null; calories: number | null }
export interface QvModifierGroup { id: number; name: QvBi; min: number; max: number; options: Array<{ id: number; name: QvBi; price_delta: number }> }
export interface QvItem {
  id: number;
  category_ids: number[];
  name: QvBi;
  description: QvBi;
  short_description: QvBi;
  price: number;
  price_from: boolean;
  was_price: number | null;
  units: QvUnit[];
  modifiers: QvModifierGroup[];
  tags: string[];
  spicy: number;
  calories: number | null;
  images: QvImage[];
  sold_out: boolean;
}
export interface QvCategory { id: number; parent_id: number | null; name: QvBi; description: QvBi; image: QvImage | null; item_ids: number[] }
export interface QvCustomTag { code: string; kind: TagKind; name: QvBi; icon: string | null; color: string | null }
export interface QvPromo { id: number; title: QvBi; text: QvBi; image: QvImage | null; link: { type: 'none' | 'category' | 'item' | 'url'; value: string | null } }
export interface QvMenu {
  restaurant: {
    slug: string;
    name: QvBi;
    branch: { id: number; name: QvBi };
    logo: string | null;
    cover: QvImage | null;
    info: { hours: QvBi; address: QvBi; map_url: string | null; phone: string | null; wifi_name: string | null; wifi_password: string | null; socials: Array<{ kind: string; url: string }> };
    default_locale: 'en' | 'ar';
  };
  settings: { show_calories: boolean; sold_out_mode: 'hide' | 'grey'; feedback_enabled: boolean };
  theme: { primary: string | null; secondary: string | null; font: string | null; card_style: string | null; radius: number | null };
  currency: { code: string; precision: number };
  menus: Array<{ id: number; name: QvBi; live: boolean }>;
  menu_id: number | null;
  categories: QvCategory[];
  items: QvItem[];
  custom_tags: QvCustomTag[];
  featured_item_ids: number[];
  promos: QvPromo[];
  resolved_at: string;
}
